<template>
<div class="container-fluid">
	<apps-toolbar title="Marketplace" icon="fa-th-large"></apps-toolbar>

	<div class="d-flex justify-content-between align-items-center mb-3">
		<h3 class="mb-0">Published Addon Apps</h3>

		<div>
			<search-bar placeholder="Search for an app..." v-on:submit="fetchAvailableApps"></search-bar>
			<button class="btn btn-primary" v-on:click="newApp" style="margin-top: -4px;">
				Publish an App...
			</button>
		</div>
	</div>

	<p v-if="availableApps.length == 0">The app store is empty :(</p>

	<p>Apps here have been written by 3rd party developers and have been published on the {{$store.appName}} platform for open use.</p>

	<div class="row no-gutters row-eq-height">
		<div v-for="(app,index) in availableApps" class="col-xl-6">
			<addon-app-card
				:is-installed="'AddonAppInstalled' in app && app.AddonAppInstalled.length > 0" 
				:app="app"
				:editable="app.publisher_user_id == $store.userDetails.id"
				:hide-uninstall-button="true"
				v-on:uninstall="uninstall(index)"
				v-on:install="installApp"
				v-on:uiLocationChange="appUiLocationChange($event,app)"
				v-on:edit="editApp(app)">
			</addon-app-card>
		</div>
	</div>

	<modal-dialog title="Publish an App"
		:modal-visible="appPublishModal.visible"
		modal-size="modal-lg"
		confirm-button-text="Publish"
		v-on:closeModal="appPublishModal.visible = false"
		v-on:confirm="saveNewApp">

		<field label="App Name" col-class="col-md-4"
			v-model="appPublishModal.payload.app_name">
			<div class="text-muted">The name that your app will be known as</div>
		</field>

		<hr>

		<field label="Search Keywords" col-class="col-md-4"
			v-model="appPublishModal.payload.search_keywords">
			<div class="text-muted">Provide some comma separated keywords to help users find the app when searching</div>
		</field>

		<field label="Description" col-class="col-md-4"
			v-model="appPublishModal.payload.description">
			<div class="text-muted">A short description describing what you app does</div>
		</field>

		<hr>

		<field label="App Version" col-class="col-md-4"
			v-model="appPublishModal.payload.app_version">
		</field>

		<hr>

		<field label="Source URL" col-class="col-md-4"
			v-model="appPublishModal.payload.src"
			:params="{type:'text',placeholder:'https://yourappdomain.com/'}">
			<div class="text-muted">A full URL to the location of your app. Your app will be injected into {{$store.appName}} via an iframe. This will serve as the iframe src.</div>
		</field>

		<hr>
		<field label="Support Website URL" col-class="col-md-4"
			v-model="appPublishModal.payload.support_website">
			<div class="text-muted">A full URL to the support website for your app, for users to visit to read documentation or find troubleshooting tips</div>
		</field>
		<hr>

		<field label="Logo Source" col-class="col-md-4"
			v-model="appPublishModal.payload.logo_src"
			:params="{type:'text',placeholder:'https://yourappdomain.com/the_app_logo.jpg'}">
			<div class="text-muted">If you want your app to have a logo, link to it here</div>
		</field>
		<hr>

		<field label="Name of Developer" col-class="col-md-4"
			v-model="appPublishModal.payload.developer_name">
		</field>
		<hr>

		<field label="Make the app public for other companies to use" col-class="col-md-9"
			v-model="appPublishModal.payload.is_public"
			:float-control-right='true'
			:params="{type:'boolean'}">

		</field>

		<div class="text-muted">If yes, other companies will see it as an available app, otherwise it will be available only to your company. Even if the app is public, your {{$store.appName}} data will not be shared.</div>

	</modal-dialog>
</div>
</template>

<script>
import AppsToolbar from "./AppsToolbar"

import ModalDialog from "@/components/ModalDialog"
import Field from "@/components/Field"
import AddonAppCard from "@/components/AddonAppCard"
import SearchBar from "@/components/SearchBar"

import AppsMixins from "@/mixins/AppsMixins.js"

export default {

	name: 'SettingsIntegrations',
	mixins: [AppsMixins],
	components: {
		AppsToolbar,
		ModalDialog,
		Field,
		AddonAppCard,
		SearchBar,
	},
	data() {
		return {
			availableApps: [],
			installedApps: [],
			appPublishModal: {
				visible: false,
				payload: {}
			}
		}
	},
	watch: {
		'$store.company'() {
			this.fetchAvailableApps()
		}
	},
	methods: {
		newApp() {
			this.appPublishModal.visible = true
			this.appPublishModal.payload = {}
		},
		editApp(app) {
			this.appPublishModal.visible = true
			this.appPublishModal.payload = this.cloneObj(app)
		},
		saveNewApp() {
			let vm = this

			this.postData("AddonApp/save",this.appPublishModal.payload,true).then(response => {
				if (response.success) {
					vm.alertSaved("Your app has been published!")
					vm.appPublishModal.visible = false
				}
			})
		},
		installApp(app_id,uiLocation) {
			let vm = this

			app.showInstallLocation = true

			let payload = {
				app_id: app_id,
				ui_location: uiLocation
			}

			this.postData("AddonAppInstalled/save",payload,true).then(response => {
				if (response.success) {
					vm.alertSaved("App installed to "+uiLocation+"!")
					vm.activeTab = "Installed Addon Apps"
					vm.fetchInstalledApps()
					vm.fetchCompanyData(false,["AddonApp"])
				}
			})
		},
		uninstall(index) {
			let vm = this

			this.uninstallApp(index).then(response => {
				vm.fetchAvailableApps()
			})
		},
		fetchAvailableApps(terms) {
			let vm = this

			let params = {
				include: ["AddonAppInstalled",{model:"User",fields:["id","slug","first_name","last_name"]}]
			}

			if (terms) {
				params.filters = [
					["app_name",terms,"or","like"],
					["search_keywords",terms,"or","like"]
				]
			}

			this.spinner(true)

			this.getJSON("AddonApp/get",params,true).then(response => {
				vm.spinner(false)

				if (response.success) {
					vm.availableApps = response.data.map(app => {

						if ('AddonAppInstalled' in app && app.AddonAppInstalled.length > 0)
							app.showInstallLocation = true
						else
							app.showInstallLocation = false

						return app
					})
				}
			})
		}
	},
	created() {
		this.fetchAvailableApps()
	}
}
</script>

<style type="text/css">
	.app-details-table th,
	.app-details-table td {
		padding: 5px;
		padding-bottom: 2px;
		padding-top: 2px;
	}

	.app-details-table th {
		text-align: right;
		padding-right: 10px;
	}
</style>